import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckIcon from '@mui/icons-material/Check'
import PasswordStrengthBar from './PasswordStrengthBar'
import type { RegisterFormData } from '../../../shared/utils/types'

interface Props {
  onBack: () => void
  onSubmit: () => void
  isSubmitting: boolean
}

export default function StepAdmin({ onBack, onSubmit, isSubmitting }: Props) {
  const { t } = useTranslation('auth')
  const [showPw, setShowPw] = useState(false)
  const {
    register,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<RegisterFormData>()

  const adminPassword = watch('adminPassword', '')

  const handleSubmit = async () => {
    const valid = await trigger(['adminNome', 'adminEmail', 'adminPassword'])
    if (valid) onSubmit()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <TextField
        label={t('register.admin.nameLabel')}
        placeholder={t('register.admin.namePlaceholder')}
        autoComplete="name"
        error={!!errors.adminNome}
        helperText={errors.adminNome?.message}
        {...register('adminNome', {
          required: t('register.admin.nameRequired'),
          minLength: { value: 2, message: t('register.admin.nameTooShort') },
        })}
      />

      <TextField
        label={t('register.admin.emailLabel')}
        type="email"
        placeholder={t('register.admin.emailPlaceholder')}
        autoComplete="email"
        error={!!errors.adminEmail}
        helperText={errors.adminEmail?.message}
        {...register('adminEmail', {
          required: t('register.admin.emailRequired'),
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: t('register.admin.emailInvalid'),
          },
        })}
      />

      <Box>
        <TextField
          label={t('register.admin.passwordLabel')}
          type={showPw ? 'text' : 'password'}
          placeholder={t('register.admin.passwordPlaceholder')}
          autoComplete="password"
          error={!!errors.adminPassword}
          helperText={errors.adminPassword?.message}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPw((v) => !v)}
                    edge="end"
                    aria-label={showPw ? t('register.admin.hidePassword') : t('register.admin.showPassword')}
                  >
                    {showPw ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }

          }}
          {...register('adminPassword', {
            required: t('register.admin.passwordRequired'),
            minLength: {
              value: 8,
              message: t('register.admin.passwordTooShort'),
            },
          })}
        />
        <PasswordStrengthBar password={adminPassword} />
      </Box>

      <Button
        variant="contained"
        size="large"
        fullWidth
        startIcon={<CheckIcon />}
        onClick={handleSubmit}
        disabled={isSubmitting}
        sx={{ mt: 0.5 }}
      >
        {isSubmitting ? t('register.admin.submitting') : t('register.admin.submit')}
      </Button>

      <Button
        variant="text"
        size="large"
        fullWidth
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        disabled={isSubmitting}
        sx={{ color: 'text.secondary' }}
      >
        {t('register.admin.back')}
      </Button>
    </Box>
  )
}
